import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { OrderStatusBadge, formatCurrency, formatDateTime } from '@/lib/utils-helpers';
import { ShoppingBag, DollarSign, Star, Clock, AlertCircle, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useBusinessType } from '@/contexts/BusinessTypeContext';

export default function VendorOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vendorStatus, setVendorStatus] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const { user } = useAuth();
  const { config } = useBusinessType();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check onboarding first
        const onboardRes = await api.get('/vendor/onboarding-status').catch(() => null);
        if (onboardRes?.data) {
          const status = (onboardRes.data.onboarding_status || '').toLowerCase();
          const step = onboardRes.data.current_step || onboardRes.data.onboarding_step || 0;
          setOnboardingStatus(status);
          
          // Redirect to onboarding only if not started or incomplete
          if (status !== 'completed' && step < 7) {
            navigate('/vendor/onboarding');
            return;
          }
        }

        const dashRes = await api.get('/vendor/dashboard');
        setData(dashRes.data);
        
        // Get vendor status from profile
        if (dashRes.data?.vendor) {
          setVendorStatus(dashRes.data.vendor.status);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [navigate]);

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}</div></div>;

  if (!data) return <p className="text-center py-12 text-muted-foreground">Unable to load dashboard</p>;

  const kpis = [
    { label: `Active ${config.ordersLabel}`, value: data.active_orders || 0, icon: ShoppingBag, color: 'text-blue-600 bg-blue-50' },
    { label: "Today's Revenue", value: formatCurrency(data.today_revenue || data.total_revenue), icon: DollarSign, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Rating', value: data.rating || data.average_rating ? Number(data.rating || data.average_rating).toFixed(1) : '0.0', icon: Star, color: 'text-amber-600 bg-amber-50' },
    { label: 'Today Deliveries', value: data.today_deliveries || data.delivered_orders || 0, icon: Clock, color: 'text-purple-600 bg-purple-50' },
  ];

  return (
    <div className="space-y-6">
      {/* Under Review Banner */}
      {(vendorStatus === 'pending' || onboardingStatus === 'completed') && vendorStatus !== 'approved' && (
        <Alert className="border-amber-200 bg-amber-50/50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between">
            <div>
              <strong className="text-amber-900">Profile Under Review</strong>
              <p className="text-sm text-amber-800 mt-1">
                Your vendor profile is being reviewed by our team. You can still manage your menu and settings, but your store is not yet visible to customers.
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/vendor/onboarding')}
              className="ml-4 shrink-0 border-amber-300 text-amber-900 hover:bg-amber-100"
            >
              <FileText className="h-4 w-4 mr-2" />
              View Status
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">
          Welcome back
        </h1>
        <p className="text-muted-foreground mt-1">Here's your store performance overview.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(kpi => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label} className="kpi-card" data-testid={`vendor-${kpi.label.toLowerCase().replace(/\s+/g, '-')}-kpi`}>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-muted-foreground">{kpi.label}</span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${kpi.color}`}><Icon className="h-4 w-4" /></div>
                </div>
                <p className="text-2xl md:text-3xl font-semibold tabular-nums" data-font="display">{kpi.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4 md:p-6">
            <h3 className="text-base font-medium mb-4">Quick Stats</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Total {config.ordersLabel}</span>
                <span className="text-sm font-medium" data-testid="vendor-total-orders">{data.total_orders || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Total Reviews</span>
                <span className="text-sm font-medium">{data.total_reviews || data.total_ratings || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">{config.itemsLabel}</span>
                <span className="text-sm font-medium" data-testid="vendor-menu-items-count">{data.menu_item_count || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 md:p-6">
            <h3 className="text-base font-medium mb-4">Pending {config.ordersLabel}</h3>
            {(data.pending_orders || []).length > 0 ? (
              <div className="space-y-2">
                {(data.pending_orders || []).map(order => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-amber-50/50 rounded-lg border border-amber-100">
                    <div>
                      <span className="text-sm font-medium">{order.user_name || 'Customer'}</span>
                      <p className="text-xs text-muted-foreground">{formatDateTime(order.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm tabular-nums font-medium">{formatCurrency(order.total)}</span>
                      <OrderStatusBadge status={order.status} />
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-center py-8 text-muted-foreground">No pending {config.ordersLabel.toLowerCase()}</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
